import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronDown,
  Download,
  Search,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ToastProvider';
import useContractsListData from '../features/contracts/useContractsListData';
import ContractsListTable from '../features/contracts/ContractsListTable';
import BulkActionsBar from '../features/contracts/BulkActionsBar';
import SavedViewsMenu from '../features/savedViews/SavedViewsMenu';
import { SAVED_VIEW_KEYS } from '../features/savedViews/savedViews';
import { FilterSearchInput } from '../components/FormComponents';

// One Export button, two formats behind it (founder consolidation,
// 2026-08-05). Popover discipline mirrors SavedViewsMenu's hard-won rules:
// Escape on a document listener that stops propagation (doc-360/doc-262),
// outside-close on mousedown, focus moves into the menu on open and back to
// the toggle on Escape (doc-328).
function ExportMenu({ exportingFormat, disabled, disabledHint, onExport }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const toggleRef = useRef(null);
  const firstItemRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) firstItemRef.current?.focus();
  }, [open]);

  const items = [
    { format: 'csv', label: 'CSV', hint: 'Download the filtered list as CSV' },
    { format: 'xlsx', label: 'Excel', hint: 'Download the filtered list as an Excel workbook (typed dates and amounts)' },
  ];

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        ref={toggleRef}
        className="btn outline"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        title={disabledHint || undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <Download className="w-4 h-4" />
        {exportingFormat ? 'Exporting…' : 'Export'}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          role="menu"
          className="card"
          style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 30, minWidth: 230, padding: 6 }}
        >
          {items.map(({ format, label, hint }, i) => (
            <button
              key={format}
              type="button"
              role="menuitem"
              ref={i === 0 ? firstItemRef : undefined}
              className="btn ghost sm"
              style={{ width: '100%', justifyContent: 'flex-start' }}
              title={hint}
              onClick={() => { setOpen(false); onExport(format); }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ContractsList() {
  const { canWriteContracts, canUploadContracts, can } = useAuth();
  const toast = useToast();
  const {
    category,
    categoryOptions,
    clearFilters,
    clearSelection,
    contracts,
    contractTypeOptions,
    deadlineFilter,
    deadlineWindow,
    deadlineWindowOptions,
    entity,
    entityOptions,
    error,
    expiringFilter,
    exportRegister,
    exportingFormat,
    group,
    groupOptions,
    handlePageSizeChange,
    hasFilters,
    loading,
    lookupsDegraded,
    needsReview,
    noticeFilter,
    page,
    pageSize,
    renewalReviewNeeded,
    setRenewalReviewNeeded,
    pageSizeOptions,
    pagination,
    refresh,
    retry,
    search,
    selectedIds,
    setCategory,
    setDeadlineFilter,
    setDeadlineWindow,
    setEntity,
    setExpiringFilter,
    setGroup,
    setNeedsReview,
    setNoticeFilter,
    setPage,
    setSearch,
    setSource,
    setStatus,
    setTag,
    setType,
    sortField,
    sortOrder,
    source,
    status,
    statusCounts,
    statusTabs,
    tag,
    tagOptions,
    toggleSelectAllOnPage,
    toggleSelected,
    toggleSort,
    type,
  } = useContractsListData();

  // Active-filter summary chips — every applied filter, each individually removable.
  const labelFor = (options, value) => options.find((o) => o.value === value)?.label || value;
  const deadlineActive = expiringFilter || noticeFilter || deadlineFilter;
  const activeFilters = [
    search.trim() && { key: 'search', label: `Search: ${search.trim()}`, onRemove: () => setSearch('') },
    status && { key: 'status', label: `Status: ${labelFor(statusTabs, status)}`, onRemove: () => setStatus('') },
    type && { key: 'type', label: `Type: ${labelFor(contractTypeOptions, type)}`, onRemove: () => setType('') },
    category && { key: 'category', label: `Category: ${labelFor(categoryOptions, category)}`, onRemove: () => setCategory('') },
    entity && { key: 'entity', label: `Entity: ${labelFor(entityOptions, entity)}`, onRemove: () => setEntity('') },
    group && { key: 'group', label: `Group: ${labelFor(groupOptions, group)}`, onRemove: () => setGroup('') },
    tag && { key: 'tag', label: `Tag: ${labelFor(tagOptions, tag)}`, onRemove: () => setTag('') },
    deadlineActive && {
      key: 'deadline',
      label: `Deadline: ${expiringFilter ? 'Expiring' : noticeFilter ? 'Notice' : 'Any'} · ${deadlineWindow}d`,
      onRemove: () => { setExpiringFilter(false); setNoticeFilter(false); setDeadlineFilter(false); },
    },
    needsReview && { key: 'needsReview', label: 'Needs review', onRemove: () => setNeedsReview(false) },
    renewalReviewNeeded && { key: 'renewalReviewNeeded', label: 'Renewal needs attention', onRemove: () => setRenewalReviewNeeded(false) },
  ].filter(Boolean);

  return (
    <div className="canvas-inner animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-eyebrow">
            {source === 'wizard' ? 'Legal review · Inbox' : 'Contracts'}
          </div>
          <h1 className="page-title">
            {source === 'wizard' ? 'Drafts awaiting legal review' : 'All contracts'}
          </h1>
          <div className="page-subtitle">
            {pagination.total} {pagination.total === 1 ? 'contract' : 'contracts'}
            {hasFilters && <> {pagination.total === 1 ? 'matches' : 'match'} filters</>}
          </div>
        </div>
        {/* Wraps: this row carries up to four controls, and .row alone would
            push the last one off a narrow viewport. */}
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {source === 'wizard' && (
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => { setSource(''); }}
            >
              Exit inbox
            </button>
          )}
          {/* Filter-aware register export (doc-116): downloads the WHOLE
              filtered set, not just the visible page. Two formats (doc-238) —
              Excel writes typed cells (real dates and numbers) and needs none
              of the formula-injection escaping CSV shows as apostrophes, so
              it's the one to hand an auditor or insurer as-is. */}
          <ExportMenu
            exportingFormat={exportingFormat}
            disabled={!!exportingFormat || loading || pagination.total === 0}
            disabledHint={
              pagination.total === 0
                ? 'Nothing to export — no contracts match the current filters'
                : null
            }
            onExport={(format) => exportRegister(format).catch((err) => toast.error('Export failed', err.message))}
          />
          {canUploadContracts && (
            <Link to="/contracts/upload" className="btn primary">
              <Upload className="w-4 h-4" />
              Upload
            </Link>
          )}
        </div>
      </div>

      {/* Search + the needs-review toggle share a row. "Needs review" is a flag
          (AI-extracted, not yet human-verified) that's orthogonal to status, so
          it's a distinct toggle rather than another dropdown. */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FilterSearchInput
            data-search-input
            className="filter-search-wide"
            icon={Search}
            placeholder="Search title, short name, counterparty, aliases, owner, notes…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {/* Named saved views (doc-316) — the filter set you rebuild every
            morning, kept under a name. Sits with the filters rather than with
            the page actions because that is what it operates on. The same menu
            is on TPRM and Reviews (doc-356), each on its own storage key. */}
        <SavedViewsMenu storageKey={SAVED_VIEW_KEYS.contracts} hasFilters={hasFilters} />
        {/* Reviewer-only: "needs review" is an internal QA queue, not something
            a read-only user can act on. */}
        {canWriteContracts && (
          <button
            type="button"
            className={needsReview ? 'btn sm toggled' : 'btn outline sm'}
            onClick={() => setNeedsReview(!needsReview)}
            style={{ whiteSpace: 'nowrap' }}
            title="Show only contracts auto-created from uploads that still need review"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Needs review
          </button>
        )}
        {/* doc-375: contracts the auto-renew sweep flagged because their new end
            date couldn't be computed — legal needs to confirm a date. */}
        {canWriteContracts && (
          <button
            type="button"
            className={renewalReviewNeeded ? 'btn sm toggled' : 'btn outline sm'}
            onClick={() => setRenewalReviewNeeded(!renewalReviewNeeded)}
            style={{ whiteSpace: 'nowrap' }}
            title="Show only auto-renewing contracts whose new end date needs confirming"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Renewal needs attention
          </button>
        )}
      </div>

      {/* Filters — plain wrapping row of dropdowns (no boxed container). Group
          and Tag always render, disabled when there's nothing to filter by.

          Option keys are prefix + value + index (doc-334). Each list is an
          "All …" placeholder with an empty value followed by facet rows, so
          keying on the value alone collapsed the placeholder and any
          empty-valued row onto one key and React dropped one of them. */}
      <div className="filter-row" style={{ marginBottom: activeFilters.length ? 10 : 16 }}>
        <select className="filter-select" value={status} onChange={e => setStatus(e.target.value)}>
          {statusTabs.map((s, i) => {
            const count = s.value ? (statusCounts[s.value] || 0) : (statusCounts.all || 0);
            return <option key={`status-${s.value || ''}-${i}`} value={s.value}>{s.label} ({count})</option>;
          })}
        </select>
        <select className="filter-select" value={type} onChange={e => setType(e.target.value)}>
          {contractTypeOptions.map((o, i) => <option key={`type-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>)}
        </select>
        <select className="filter-select" value={category} onChange={e => setCategory(e.target.value)}>
          {categoryOptions.map((o, i) => <option key={`cat-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>)}
        </select>
        <select className="filter-select" value={entity} onChange={e => setEntity(e.target.value)}>
          {entityOptions.map((o, i) => <option key={`ent-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>)}
        </select>
        <select
          className="filter-select"
          value={group}
          onChange={e => setGroup(e.target.value)}
          disabled={groupOptions.length <= 1}
        >
          {groupOptions.map((o, i) => <option key={`grp-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>)}
        </select>
        <select
          className="filter-select"
          value={tag}
          onChange={e => setTag(e.target.value)}
          disabled={tagOptions.length <= 1}
        >
          {tagOptions.map((o, i) => <option key={`tag-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>)}
        </select>

        {/* Deadline kind + window are ONE logical control, so they share a
            .filter-pair cell — otherwise the window wraps alone to the far left
            of the next row while the kind sits at the far right of this one. */}
        <div className={deadlineActive ? 'filter-pair two-up' : 'filter-pair'}>
          {/* Deadline kind — one mutually-exclusive select; yellow tint when set.
              The window it applies over is the sibling select below, so the two
              stay a 3×3 pair instead of nine flattened options. */}
          <select
            className={deadlineActive ? 'filter-select is-active' : 'filter-select'}
            value={
              deadlineFilter ? 'deadline'
                : expiringFilter ? 'expiring'
                : noticeFilter ? 'notice'
                : ''
            }
            onChange={(e) => {
              const v = e.target.value;
              setExpiringFilter(v === 'expiring');
              setNoticeFilter(v === 'notice');
              setDeadlineFilter(v === 'deadline');
            }}
            title="Filter by upcoming deadline"
          >
            <option value="">All deadlines</option>
            <option value="deadline">Any deadline</option>
            <option value="expiring">Expiring</option>
            <option value="notice">Notice</option>
          </select>

          {/* How far ahead to look. Only meaningful once a deadline kind is
              picked, so it stays hidden until then rather than sitting inert. */}
          {deadlineActive && (
            <select
              className="filter-select is-active"
              value={deadlineWindow}
              onChange={(e) => setDeadlineWindow(parseInt(e.target.value, 10))}
              aria-label="Deadline window"
              title="How far ahead to look for deadlines"
            >
              {deadlineWindowOptions.map((days) => (
                <option key={days} value={days}>Next {days} days</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {lookupsDegraded && (
        <div style={{ fontSize: 12, marginBottom: 12, color: 'var(--text-yellow)' }}>
          Some filter options couldn't be loaded — the type, group, and tag dropdowns may be incomplete. Refresh to retry.
        </div>
      )}

      {/* Active filter chips — what's applied, each individually removable */}
      {activeFilters.length > 0 && (
        <div className="active-filters" style={{ marginBottom: 16 }}>
          {activeFilters.map(f => (
            <span key={f.key} className="active-filter-chip">
              {f.label}
              <button type="button" onClick={f.onRemove} aria-label={`Remove ${f.label}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button className="btn ghost xs" onClick={clearFilters}>
            <X className="w-3 h-3" />
            Clear all
          </button>
        </div>
      )}

      <ContractsListTable
        canWriteContracts={canWriteContracts}
        clearFilters={clearFilters}
        contracts={contracts}
        error={error}
        handlePageSizeChange={handlePageSizeChange}
        hasFilters={hasFilters}
        loading={loading}
        onRetry={retry}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        pagination={pagination}
        search={search}
        selectedIds={selectedIds}
        setPage={setPage}
        setTag={setTag}
        sortField={sortField}
        sortOrder={sortOrder}
        toggleSelectAllOnPage={toggleSelectAllOnPage}
        toggleSelected={toggleSelected}
        toggleSort={toggleSort}
      />

      {/* Bulk actions over the selection. Download is open to every user
          (per-contract visibility enforced server-side); the write actions
          require write_contracts, and extractions require run_schemas. */}
      <BulkActionsBar
        canRunSchemas={can('run_schemas')}
        canWriteContracts={canWriteContracts}
        selectedIds={selectedIds}
        tagOptions={tagOptions}
        contracts={contracts}
        onClearSelection={clearSelection}
        onApplied={refresh}
      />

    </div>
  );
}
